import {GridLegacy as Grid, Container} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {alpha} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {MulitpleAssets, Poll, SingleAssets} from '@perfectpost/perfect-post-common';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, NavLink, useParams} from 'react-router-dom';

import noPic from '../assets/images/nopic.png';
import logo from '../assets/images/pp_icon512.png';
import {ActivityPreview} from '../components/activitypreview/ActivityPreview';
import {usePublicDraftQuery} from '../hooks/draft/queries/usePublicDraftQuery';
import {useOneUserQuery} from '../hooks/users/queries/useOneUserQuery';
import {useDocumentTitle} from '../useDocumentTitle';

export function DraftSharing() {
  const {id} = useParams();
  const {t} = useTranslation();
  useDocumentTitle(t('draft.documenttitle'));

  const {data: draft} = usePublicDraftQuery(id);
  // L'auteur n'est lu qu'une fois le brouillon connu, comme avant (appels enchaînés).
  const {data: user} = useOneUserQuery(draft?.user);
  const sharingUrl: string | string[] | undefined = useMemo(() => {
    if (draft?.sharing === undefined || draft.sharing.type === 'poll') {
      return undefined;
    }
    return draft.sharing.type === 'images' ? draft.sharing.images.map((image) => image.path) : draft.sharing.path;
  }, [draft?.sharing]);

  const sharing: SingleAssets | MulitpleAssets | Poll | undefined = useMemo(() => {
    if (
      (draft?.linkedin?.carrousel || draft?.linkedin?.image || draft?.linkedin?.video) &&
      draft?.sharing === undefined
    ) {
      const singleAssets: SingleAssets = {
        type: draft.linkedin.video ? 'video' : draft.linkedin.image ? 'image' : 'pdf',
        path: draft.linkedin.image ?? draft.linkedin.video ?? draft.linkedin.carrousel ?? '',
      };
      return singleAssets;
    } else if (draft?.sharing !== undefined) {
      return draft?.sharing && draft.sharing.type !== 'poll' && draft.sharing.type !== 'images'
        ? {...draft.sharing, url: sharingUrl ?? ''}
        : draft?.sharing;
    } else {
      return undefined;
    }
  }, [draft?.sharing, sharingUrl]);

  return (
    <Box px={3}>
      <Container>
        <Box
          sx={() => ({
            py: 1,
            px: {xs: 5, sm: 5, lg: 5},
            my: 2,
            mx: 3,
            width: 'calc(100% - 48px)',
            borderRadius: 'section',
            shadow: 'md',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            left: 0,
            zIndex: 3,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.5),
            backdropFilter: `saturate(200%) blur(30px)`,
          })}
        >
          {/* Lien sans la couleur ni le soulignement du navigateur : bleu illisible en sombre. */}
          <Box
            component={NavLink}
            to="/signin"
            display="flex"
            alignItems="center"
            sx={{color: 'text.primary', textDecoration: 'none'}}
          >
            <Box component="img" src={logo} alt="PerfectPost Logo" width="2rem" />
            <Box ml="2">
              <Typography component="h6" variant="button" fontWeight="medium" color="text.primary">
                PerfectPost
              </Typography>
            </Box>
          </Box>
          <Box display={{xs: 'none', lg: 'inline-block'}}>
            <Button
              component={Link}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              to="/signin"
              target="_blank"
              rel="noreferrer"
              color="info"
              size="small"
            >
              {t('draft.sharing.signin')}
            </Button>
          </Box>
        </Box>
      </Container>
      <Grid container spacing={2}>
        <Grid item sm={false} lg={3} />
        <Grid item sm={12} lg={6}>
          {user && (
            <ActivityPreview
              fullname={user !== undefined && 'firstname' in user ? `${user.firstname} ${user.lastname}` : ''}
              picture={user.lnPicture ?? noPic}
              content={draft?.content ?? ''}
              headline={user !== undefined && 'firstname' in user ? user?.headline : undefined}
              sharing={sharing}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
